/** @jsx jsx */
import { Flex, Grid, Heading, InfoBox, InformationIcon, Link, Paragraph, Status, Tooltip } from "@eworkit/iris";
import { forwardRef, useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";
import { jsx } from "theme-ui";

import { UserContext } from "@/components/User";
import { extractPayExpressInfoFromJRA } from "@/data/job-applications";
import { JobApplication } from "@/data/job-applications/types/JobApplication";
import { JobRequest } from "@/data/job-requests/types/JobRequest";
import { DEFAULT_PAYMENT_TERM_REDUCTION } from "@/data/payexpress/service";
import { useFormats } from "@/hooks/Formats";

import { useApplyWizard } from "../apply/_components/ApplyWizardContext";

interface PaymentDetailsSummaryProps {
  jobRequest: JobRequest;
  jobApplication: JobApplication;
  paymentError?: string;
}

export const JobApplicationPaymentDetailsSummary = forwardRef<HTMLDivElement, PaymentDetailsSummaryProps>(
  ({ jobRequest, jobApplication, paymentError }, ref) => {
    const { t } = useTranslation("session");
    const { formatRate } = useFormats();

    const User = useContext(UserContext);

    const payExpressData = extractPayExpressInfoFromJRA(jobApplication);

    const usePayExpress = payExpressData.payExpressSelected;
    const payExpressEnabled = jobRequest.payExpressConfig?.payExpressEnabled ?? false;

    const showLegacyPaymentTerm = Boolean(jobApplication.payExpress?.paymentTerm);

    const payExpressFeePercentage = payExpressData.payExpressFeePercentage;
    // const payExpressFee = jobApplication.costs?.payExpressFeeAmount;

    const supplierPaymentTerm = payExpressData.supplierPaymentTerm;

    const paymentTerm = usePayExpress ? DEFAULT_PAYMENT_TERM_REDUCTION : supplierPaymentTerm;

    const { goToStepByKey } = useApplyWizard();

    const handleGoToDetailsLinkClick = useCallback(() => {
      goToStepByKey("DETAILS");
    }, [goToStepByKey]);

    return (
      <Flex ref={ref} flexDirection="column" gap="sm" py="md">
        <Flex gap="xs" alignItems="baseline">
          <Heading size="h-sm">{t("JOB_APPLICATIONS.DETAIL.SECTION_DETAILS.PAYMENT")}</Heading>
          {usePayExpress && (supplierPaymentTerm === 0 || jobRequest.rate.clientRateType === "FIXED") && (
            <Status variant="warning" size="md">
              {t("JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.PAYEXPRESS.INTERESTED_IN_PAYEXPRESS")}
            </Status>
          )}
        </Flex>
        <Grid columns={["1fr", null, "1fr 3fr"]} gap={["xxs", null, "sm"]}>
          <Grid>
            <Paragraph color="n700">{t("JOB_APPLICATIONS.DETAIL.SECTION_DETAILS.HOURLY_RATE")}</Paragraph>
          </Grid>
          <Grid>
            <Paragraph data-test="rate">
              {jobApplication!.rate && jobApplication!.rate.currency
                ? formatRate(
                    jobApplication!.rate.ratePerHour || 0,
                    jobApplication!.rate.currency,
                    jobRequest.rate.clientRateType === "HOURLY" ? "h" : undefined,
                    {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    },
                  )
                : t("JOB_APPLICATIONS.DETAIL.EMPTY")}
            </Paragraph>
          </Grid>
        </Grid>

        {((payExpressEnabled &&
          Boolean(paymentTerm) &&
          supplierPaymentTerm !== 0 &&
          User.contextCompany &&
          jobRequest.rate.clientRateType !== "FIXED") ||
          showLegacyPaymentTerm) && (
          <Grid columns={["1fr", null, "1fr 3fr"]} gap={["xxs", null, "sm"]}>
            <Grid>
              <Paragraph color="n700">
                {t("JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.PAYEXPRESS.PAYMENT_TERM")}
              </Paragraph>
            </Grid>
            <Grid>
              <Paragraph data-test="term">
                {t("JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.PAYEXPRESS.PAYMENT_TERM_DAYS", {
                  count: paymentTerm!,
                })}
              </Paragraph>
            </Grid>
          </Grid>
        )}

        {Boolean(usePayExpress && payExpressFeePercentage && jobRequest.rate.clientRateType !== "FIXED") && (
          <Grid columns={["1fr", null, "1fr 3fr"]} gap={["xxs", null, "sm"]}>
            <Grid>
              <Flex gap="xs" alignItems="center">
                <Paragraph color="n700">
                  {t("JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.PAYEXPRESS.PAYEXPRESS_FEE")}
                  <Tooltip
                    content={t("JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.PAYEXPRESS.PAYEXPRESS_FEE_INFO_NET", {
                      fee: payExpressFeePercentage,
                      term: paymentTerm!,
                    })}>
                    {tooltipProps => (
                      <InformationIcon
                        color="n700"
                        size="xs"
                        sx={{ ml: "xxs", verticalAlign: "middle" }}
                        {...tooltipProps}
                      />
                    )}
                  </Tooltip>
                </Paragraph>
              </Flex>
            </Grid>
            <Grid>
              <Paragraph data-test="payExpressFee">{payExpressFeePercentage} %</Paragraph>
            </Grid>
          </Grid>
        )}

        {Boolean(jobApplication!.costs?.brokerFeePercentage) && (
          <Grid columns={["1fr", null, "1fr 3fr"]} gap={["xxs", null, "sm"]}>
            <Grid>
              <Flex gap="xs" alignItems="center">
                <Paragraph color="n700">
                  {t("JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.FEES.EWORK_SERVICES_FEE")}
                  <Tooltip content={jobApplication?.jobRequest?.brokerFee?.description}>
                    {tooltipProps => (
                      <InformationIcon
                        color="n700"
                        size="xs"
                        sx={{ ml: "xxs", verticalAlign: "middle" }}
                        {...tooltipProps}
                      />
                    )}
                  </Tooltip>
                </Paragraph>
              </Flex>
            </Grid>
            <Grid>
              <Paragraph data-test="brokerFee">{jobApplication!.costs?.brokerFeePercentage} %</Paragraph>
            </Grid>
          </Grid>
        )}

        {Boolean(jobApplication!.costs?.brokerFeeAmount || Boolean(jobApplication!.costs?.payExpressFeeAmount)) &&
          jobRequest.rate.clientRateType !== "FIXED" && (
            <Grid columns={["1fr", null, "1fr 3fr"]} gap={["xxs", null, "sm"]}>
              <Grid>
                <Paragraph color="n700">
                  {t("JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.PAYEXPRESS.TOTAL_PAYOUT")}
                </Paragraph>
              </Grid>
              <Grid>
                <Paragraph data-test="totalPaidOut" bold>
                  {jobApplication!.rate!.currency
                    ? formatRate(jobApplication!.costs?.finalRatePerHour || 0, jobApplication!.rate!.currency, "h", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })
                    : t("JOB_APPLICATIONS.DETAIL.EMPTY")}
                </Paragraph>
              </Grid>
            </Grid>
          )}

        {paymentError && (
          <InfoBox
            variant="warning"
            text={t(`JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.FEES.${paymentError}`)}
            component={
              <div>
                <Link as="button" onClick={handleGoToDetailsLinkClick}>
                  {t("JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.FEES.GO_TO_DETAILS")}
                </Link>
              </div>
            }
          />
        )}
      </Flex>
    );
  },
);
